import type { CatalogMuscleSlug } from "./muscleCatalog.js";
import {
  computeMuscleAdjacency,
  type AdjacencyDay,
  type MuscleAdjacencyWarning,
} from "./muscleAdjacency.js";
import { DAY_MS } from "./freshness.js";

// Logged-session adapter for the muscle-adjacency check. Where the program
// adapter works on a cyclic template, this works on the LINEAR stream of
// sessions the athlete actually logged: it flags a muscle trained on two
// consecutive CALENDAR days. No wrap — a logged history doesn't repeat.
//
// The distance axis is the epoch-day (UTC) of each session's start, so two
// sessions on back-to-back days sit one position apart and trip the Δ=1
// trigger; two sessions on the same day collapse to distance 0 and are
// ignored by the core (one day, not an adjacency). The caller resolves the
// muscles however it has them — from a SessionResult's volumeByMuscle keys,
// or from each set's exercise via the catalog — so this stays decoupled
// from the catalog. Caveat: UTC day-bucketing can misplace a workout logged
// late local-evening by one day; acceptable for an advisory signal.

export interface SessionDayInput {
  /** Stable id (session id). */
  key: string;
  /** Human label, typically the date (e.g. "2026-06-20"). */
  label: string;
  /** Session start in epoch-ms; bucketed to a UTC day for the distance. */
  startedAtMs: number;
  /** Muscles the session trained, de-duplicated by the Set. */
  muscles: Iterable<CatalogMuscleSlug>;
}

export const sessionsToAdjacencyDays = (
  sessions: ReadonlyArray<SessionDayInput>,
): AdjacencyDay[] =>
  sessions.map((s) => ({
    key: s.key,
    label: s.label,
    position: Math.floor(s.startedAtMs / DAY_MS),
    muscles: new Set(s.muscles),
  }));

export const checkSessionMuscleAdjacency = (
  sessions: ReadonlyArray<SessionDayInput>,
): MuscleAdjacencyWarning[] =>
  computeMuscleAdjacency(sessionsToAdjacencyDays(sessions), { cyclic: false });
